import React, { forwardRef, useCallback, useContext, useEffect } from 'react';

import { LayoutContainer } from '@zooplus/ui-components';

import AppContext from '../../context';
import { CategoryTypeEnum, UiCategory } from '../../types';

import AnimalFlyout from './AnimalFlyout';
import { AnimalFlyoutV2 } from './AnimalFlyoutV2';
import styles from './CategoryFlyout.module.scss';
import SpecialOffersFlyout from './SpecialOffersFlyout';
import TopBrandsFlyout from './TopBrandsFlyout';

interface CategoryFlyoutWithoutRefProps {
    category: UiCategory;
    show: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onBlurFirst: () => void;
    onBlurLast: () => void;
}

export interface CategoryFlyoutProps extends CategoryFlyoutWithoutRefProps {
    forwardedRef: React.Ref<HTMLDivElement>;
}

export function CategoryFlyoutWithoutRef(
    props: CategoryFlyoutProps,
): JSX.Element {
    const { isArtificialCategoriesEnabled } = useContext(AppContext);
    const { category, show, forwardedRef, onMouseEnter, onMouseLeave } = props;
    const { type } = category;

    const activeElementIsContained = useCallback(() => {
        return new Promise((resolve) => {
            setImmediate(() => {
                if (forwardedRef) {
                    resolve(
                        (
                            forwardedRef as React.MutableRefObject<HTMLDivElement>
                        ).current.contains(document.activeElement),
                    );
                }
                resolve(false);
            });
        });
    }, [forwardedRef]);

    const onBlurFirstItem = useCallback(async () => {
        const { onBlurFirst } = props;
        const isContained = await activeElementIsContained();
        if (!isContained) {
            onBlurFirst();
        }
    }, [activeElementIsContained, props]);

    const onBlurLastItem = useCallback(async () => {
        const { onBlurLast } = props;
        const isContained = await activeElementIsContained();
        if (!isContained) {
            onBlurLast();
        }
    }, [activeElementIsContained, props]);

    const onEscape = useCallback(
        (event: KeyboardEvent) => {
            const { onBlurFirst } = props;
            if (event.key === 'Escape') {
                onBlurFirst();
            }
        },
        [props],
    );

    useEffect(() => {
        if (forwardedRef) {
            const all = (
                forwardedRef as React.MutableRefObject<HTMLDivElement>
            ).current.querySelectorAll('a');
            if (all) {
                const first = all[0];
                const last = all[all.length - 1];
                if (show) {
                    first.addEventListener('focusout', () => onBlurFirstItem);
                    last.addEventListener('focusout', () => onBlurLastItem);
                    document.addEventListener('keyup', onEscape);
                } else {
                    first.removeEventListener(
                        'focusout',
                        () => onBlurFirstItem,
                    );
                    last.removeEventListener('focusout', () => onBlurLastItem);
                    document.removeEventListener('keyup', onEscape);
                }
            }
        }
    }, [forwardedRef, onBlurFirstItem, onBlurLastItem, onEscape, show]);

    return (
        <LayoutContainer
            variant="default"
            className={styles.flyOut}
            ref={forwardedRef}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            id="header-category-flyout"
            style={
                {
                    '--header-category-flyout-display-flag': `${
                        show ? 'block' : 'none'
                    }`,
                } as React.CSSProperties
            }>
            <div className={styles.flyOutContainer}>
                {(type === CategoryTypeEnum.ANIMAL ||
                    type === CategoryTypeEnum.NORMAL) && (
                    <>
                        {isArtificialCategoriesEnabled ? (
                            <AnimalFlyoutV2 category={category} />
                        ) : (
                            <AnimalFlyout category={category} />
                        )}
                    </>
                )}

                {type === CategoryTypeEnum.BRANDS && (
                    <TopBrandsFlyout category={category} />
                )}

                {(type === CategoryTypeEnum.OFFERS ||
                    type === CategoryTypeEnum.OUTLET) && (
                    <SpecialOffersFlyout category={category} />
                )}
            </div>
        </LayoutContainer>
    );
}

const CategoryFlyoutWithRef = (
    props: CategoryFlyoutWithoutRefProps,
    ref: React.Ref<HTMLDivElement>,
) => {
    const {
        category,
        show,
        onMouseEnter,
        onMouseLeave,
        onBlurLast,
        onBlurFirst,
    } = props;
    return (
        <CategoryFlyoutWithoutRef
            category={category}
            forwardedRef={ref}
            onBlurFirst={onBlurFirst}
            onBlurLast={onBlurLast}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            show={show}
        />
    );
};

export default forwardRef(CategoryFlyoutWithRef);
